import { ApiSimpleComponent } from "@incendium/api";
import { Preview } from "@mui/icons-material";
import Delete from "@mui/icons-material/Delete";
import Edit from "@mui/icons-material/Edit";
import {
  Button,
  IconButton,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  useTheme,
  useMediaQuery,
  Tooltip,
} from "@mui/material";
import { CenterPage } from "Components/CenterPage/CenterPage";
import Loading from "Components/Loading/Loading";
import SpacedList from "Components/UI/SpacedList";
import { cell3Icons } from "consts";
import { deleteComponent } from "features/campaigns";
import { useLanderTemplates } from "features/lightningLanders/hooks/useLanderTemplates";
import { useSelectedProject } from "Hooks";
import { useConfirmation } from "Hooks/useConfirmation";
import produce from "immer";
import { Suspense, useCallback, useState } from "react";
import { useHistory } from "react-router-dom";
import LanderPreviewDialog from "features/lightningLanders/components/LanderPreviewDialog";

function LightningTemplatesPage() {
  const { selectedProject } = useSelectedProject();
  const { templates, setTemplates, fetched } = useLanderTemplates();
  const history = useHistory();
  const handleDelete = useConfirmation();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));

  const [previewTemplate, setPreviewTemplate] = useState<ApiSimpleComponent | null>(null);

  const newTemplate = useCallback(() => {
    history.push(`./lightning-landing-templates/new`);
  }, [history]);

  const editLandingTemplate = useCallback(
    (template) => {
      history.push(`./lightning-landing-templates/${template.id}`);
    },
    [history]
  );

  const onPreview = useCallback((template: ApiSimpleComponent) => {
    setPreviewTemplate(template);
  }, []);

  const handleClosePreview = useCallback(() => {
    setPreviewTemplate(null);
  }, []);

  const onDelete = useCallback(
    (template: ApiSimpleComponent) => {
      handleDelete({
        title: "Are you sure you want to delete this Template",
        body: `This will not affect any landing page you have created with this template, but it will not be available for new landers`,
        callback: async () => {
          await deleteComponent(selectedProject?.id, template.id);

          setTemplates((components) =>
            produce(components, (draft) => {
              const idx = draft.findIndex((d) => d.id === template.id);
              if (idx >= 0) {
                draft.splice(idx, 1);
              }
            })
          );

          return `Template ${template.name} deleted`;
        },
      });
    },
    [handleDelete, selectedProject, setTemplates]
  );

  return (
    <>
      {!fetched ? (
        <Loading />
      ) : (
        <Suspense fallback={<Loading />}>
          {templates.length === 0 ? (
            <CenterPage calcHeight>
              <Typography variant="subtitle1">
                You don't have any Templates yet.
              </Typography>
              <Typography variant="subtitle1" mb={2} color={"secondary"}>
                Click the button below to get started!
              </Typography>
              <Button onClick={newTemplate}>New Lightning Template</Button>
            </CenterPage>
          ) : (
            <SpacedList
              title="Lightning Templates"
              action={
                <Button onClick={newTemplate}>New Lightning Template</Button>
              }
            >
              <TableHead>
                <TableRow>
                  <TableCell>Template Name</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {templates.map((template) => (
                  <TableRow key={template.id}>
                    <TableCell>{template.name}</TableCell>
                    <TableCell>{template.description}</TableCell>
                    <TableCell align="right" style={{ width: cell3Icons }}>
                      <Tooltip title={`Preview ${template.name}`}>
                        <IconButton onClick={() => onPreview(template)}>
                          <Preview />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={`Edit ${template.name}`}>
                        <IconButton onClick={() => editLandingTemplate(template)}>
                          <Edit />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={`Delete ${template.name}`}>
                        <IconButton onClick={() => onDelete(template)}>
                          <Delete />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </SpacedList>
          )}

          {previewTemplate && (
            <LanderPreviewDialog
              open={!!previewTemplate}
              onClose={handleClosePreview}
              component={previewTemplate}
              title={`${previewTemplate.name} - Preview`}
              fullScreen={fullScreen}
            />
          )}
        </Suspense>
      )}
    </>
  );
}

export default LightningTemplatesPage;
